import { normalizeMembershipRoles, normalizeRetainedRoles, roleCapabilities } from './models/roles';
import type { FSRefJson } from './fs/FSRef';
import { v4 as uuidv4 } from 'uuid';
import { ActionInfo } from './models/ActionInfo';
import { ActionType, JSONSchemaParser } from './FlowSync/schema';
import { ExpansionType } from './FlowSync/expand';
import { EntityExpansion } from './FlowSync/store';
import { EntityTypes } from './schema/types';
import type { IWorkspace, Workspace } from './entities/workspace';
// Aliased: a bare `Record` import shadows the global `Record<K,V>` utility for
// this entire file, so every `Record<string, unknown>` in it resolved to the
// FS record class and failed as a non-generic type.
import { Record as FsRecord, RecordRefs } from './fs/Record';
import { EntityFactory, type EntityConstructor } from './schema/factory';
import { ExpansionRequest, QueryRequest } from './FlowSync/query';
import { DataManager, Manageable } from './FlowSync/store';
import { FlowData, FlowDataStream } from './flow_processing';
import { defaultEntityType, IEntity } from './IEntity';
import { DockPointerData, type IDockPointer } from './models/DockPointer';
import type { ShowTarget } from './models/ShowTarget';
import { editorForType } from './models/asset-editor';
import { TypeId } from './models/TypeId';
import { ViewType } from './utils/ui/view-types';
import { normalizeEmail } from './utils/utils';
import { Callable } from './types/callable';

/**
 * The DisplayTarget an `install()` / `setup()` returns — what the receiver should
 * navigate to (`openDisplayTarget`). `kind:'entity'` pointing at an
 * `agentic_process` is a spawned Vibe setup session; other entities open in
 * their editor; `webapp` opens the port preview.
 *
 * An alias for {@link ShowTarget}, not a fourth declaration of it. The backend
 * builds both from the same place — `Entity.setup_on_receive`'s
 * `_entity_payload` is the builder `resolve_display_target` uses — and this
 * copy had already fallen behind by `artifact_id`, `micro_app_id`, `runtime`
 * and `name`, so a `kind:'app'` target arriving here was typed as though it had
 * no artifact or app id at all.
 */
export type ReceiveShowTarget = ShowTarget;

/**
 * One row of an entity's member roster, as returned by the generic ``members``
 * action (``APIEntity.fetchMembers``). The hub normalizes ``user_email`` →
 * ``email`` etc. server-side (see ``_hub_reflect.normalize_hub_response``);
 * extra hub keys (``status``, ``role``, ``invitation_id``, …) pass through, so
 * this is intentionally open. ``Participant`` in ``entities/members.ts`` is the
 * structurally-compatible alias kept for existing import sites. */
export interface EntityMember {
  user_id?: string | null;
  email?: string | null;
  name?: string | null;
  roles?: string[] | null;
  status?: string | null;
  [key: string]: unknown;
}

/**
 * The hub's roster keys (``user_email`` / ``user_name`` / ``user_picture``) in the client form.
 * The local server does this in ``normalize_hub_response``, but a hub-only frontend reads the hub
 * directly and skips it — so an invited user with no name rendered as "unknown".
 */
export function normalizeMember(m: EntityMember): EntityMember {
  return {
    ...m,
    email: m.email ?? (m.user_email as string | null | undefined) ?? null,
    name: m.name ?? (m.user_name as string | null | undefined) ?? null,
    picture: m.picture ?? m.user_picture ?? null,
  };
}

/** Why a copy exists on disk, as classified by the backend path rules. */
export type AssetOccurrenceOrigin = 'installed_package' | 'dependency' | 'local';

/** Which signal separated the primary from the runner-up. Primary row only. */
export type AssetRankBasis = 'git' | 'created' | 'first_seen' | 'path';

/**
 * One backend-ranked on-disk occurrence of an asset identity, with the
 * evidence that ranked it. Evidence is present only on collided assets — a
 * lone occurrence has nothing to explain — so every field past `first_seen_at`
 * is optional and the UI must render around its absence.
 */
export interface AssetOccurrence {
  path: string;
  /** When FlowPad first indexed this path. */
  first_seen_at: string;
  /** Git introduction time, when the path is tracked in a repo. */
  introduced_at?: string;
  /** Filesystem birth time, when the filesystem reports a trustworthy one. */
  birth_time?: string;
  /** Omitted when `local`. */
  origin?: AssetOccurrenceOrigin;
  /** Set on the primary occurrence only. */
  rank_basis?: AssetRankBasis;
}
import { defineGlobal } from './utils/globals';
import { WikiLink } from './types/wiki';
import type { HttpMethod } from './models/ApiUrl';
import { deepEqual } from './utils/deep-equal';

/**
 * True when ``v`` is a string with at least one non-whitespace character.
 * Wire payloads occasionally carry non-string values for nominally-string
 * fields (legacy records, unmigrated data); the type guard keeps display-chain
 * call-sites from blowing up on `.trim()`.
 */
export function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

/** One person a ``share`` invites: a bare ``idOrEmail``, or ``{idOrEmail, roles?}``. */
export type ShareInvitee = string | { idOrEmail: string; roles?: string[] };

/**
 * ``users`` as the ``share`` action's ``recipients`` wire list — trimmed, blanks
 * dropped, a role only when there is one (``ShareRequestSpec`` in
 * ``flow_sdk/app/actions/share_action.py`` validates it).
 */
export function shareRecipients(users: ShareInvitee[]): (string | { idOrEmail: string; roles: string[] })[] {
  return users.flatMap(u => {
    const { idOrEmail, roles } = typeof u === 'string' ? { idOrEmail: u, roles: undefined } : u;
    const trimmed = idOrEmail.trim();
    return trimmed ? [roles ? { idOrEmail: trimmed, roles: normalizeMembershipRoles(roles) } : trimmed] : [];
  });
}

/**
 * While an entity is being constructed (the rest of the current task), the cached instance it replaced
 * under the same id — so its field writes notify only what really changed. See `getProxy`'s `set`.
 */
const replacedByConstruction = new WeakMap<object, Record<PropertyKey, unknown>>();

/** The write repeats the replaced instance's value. Entity-valued fields compare by reference, never by walking the graph. */
function sameAsReplaced(target: object, property: PropertyKey, value: unknown): boolean {
  const replaced = replacedByConstruction.get(target);
  if (replaced === undefined) return false;
  const old = replaced[property];
  if (old === value) return true;
  const isEntity = (v: unknown) => typeof v === 'object' && v !== null && 'typeId' in v;
  if (isEntity(old) || isEntity(value)) return false;
  return deepEqual(old, value);
}

export function getProxy<T extends Manageable & { [key: string | symbol]: any }>(target: T) {
  return new Proxy(target, {
    get(target, property, receiver) {
      // Implement any specific logic needed when properties are accessed
      return Reflect.get(target, property, receiver);
    },
    set(target, property, value, receiver) {
      const oldValue = target[property];
      const result = Reflect.set(target, property, value, receiver);
      if (result && oldValue !== value) {
        // Skip dirty + notify for internal fields. Underscore-prefixed
        // properties (e.g. `_flowDataStream` lazy-init holder, `_dirty`,
        // `_data` caches) are private to the entity — they are not part
        // of the persisted schema and consumers do not subscribe to them.
        // Firing notifyPropertyChanged for them is what triggered the
        // render-phase "Cannot update component while rendering different
        // component" warning: a getter-with-side-effects (e.g. `flowDataStream`)
        // mutated `_flowDataStream` during a sibling component's render and
        // synchronously dispatched setState across all subscribers.
        const isInternal = typeof property === 'string' && property.startsWith('_');
        // A constructor writing the same data the replaced cached instance already held is not a change:
        // re-notifying it re-rendered every subscriber, and a component that built an entity while
        // rendering re-rendered itself forever (the 2026-10-23 freeze). Real changes still notify.
        if (!isInternal && property !== 'dirty' && !sameAsReplaced(target, property, value)) {
          target.dirty = true;
          dataManager.notifyPropertyChanged(target.typeId, property as string);
        }
      }
      return result;
    },
  });
}

export const registerEntity = (constructor: EntityConstructor) => {
  EntityFactory.registerEntity(constructor);
};

/**
 * Persisted fields declared by this class itself — the shared columns every
 * entity carries (Python ``DBBaseRecord`` + ``Entity``). They are db fields no
 * matter what a per-type schema says, because the type schema only describes
 * that type's OWN fields. See ``isDbField``.
 *
 * Read-only/computed slots (``expand``, ``members``, ``asset_occurrences``,
 * ``duplicate_count``, ``orphan*``) stay OUT — they're server-owned and must
 * not be echoed back on save.
 */
const BASE_WIRE_FIELDS = new Set<string>([
  'id',
  'uname',
  'name',
  'title',
  'key',
  'namespace',
  'tags',
  'labels',
  'system',
  'created_by',
  'created_date',
  'updated_by',
  'updated_date',
  'created_through',
  'updated_through',
  'schema_version',
  'root_vfs_path',
  'fs_storage_mount_path',
  'visitor_roles', 'authenticated_roles', 'public_roles',
  'last_edited_at',
]);

/** Backend-owned fields that hydrate into the cache but never ride a full save. */
const SERVER_MANAGED_SAVE_FIELDS = new Set<string>(['last_edited_at']);

/**
 * Any entity, without caring which.
 *
 * `APIEntity<T>` is F-bounded — `T` is the SELF type (`clone(): T`,
 * `save(): Promise<T>`) — so the obvious spellings do not work: `APIEntity<never>`
 * makes nothing assignable, and `APIEntity<unknown>` does not satisfy its own
 * bound. `any` is the one type argument that does, which is why it was already
 * hand-written at ~40 sites. Named once so those sites say what they mean
 * instead of each re-deriving the trick.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyEntity = APIEntity<any>;

export class APIEntity<T extends APIEntity<T>> implements IEntity, Manageable {
  static type?: string = defaultEntityType;
  static autoLoadExpansions: ExpansionType[] = [];
  static icon: string | null = null;
  // Shared fields lifted from Python ``DBBaseRecord`` + ``Entity``. All
  // optional — subclasses populate the ones they care about. ``deepAssign``
  // in the base constructor copies wire fields onto the instance, so
  // subclasses no longer need to redeclare or manually assign these.
  id: string;
  /** The wire's own `type` discriminator, copied onto the instance by
   *  `deepAssign` (absent on an entity constructed without one). Distinct from
   *  the static `type` the class registers under — `getType()` reads that. */
  type?: string;
  uname?: string;
  // Nullable: the wire sends null for an unset name/title, and subclasses
  // (Shell, Conversation) declare them that way. The base must admit it or
  // every such subclass fails its own `APIEntity<T>` F-bound.
  name?: string | null;
  title?: string | null;
  key?: string;
  namespace?: string;
  tags?: string[];
  system?: boolean;
  /**
   * Backend FSIndexer flag: true when the on-disk Record this entity points
   * at can no longer be located. ``deepAssign`` in the constructor copies
   * this off the wire JSON; subclasses don't need to redeclare.
   */
  orphan?: boolean;
  /** ISO 8601 timestamp of the last ``orphan = true`` transition; null otherwise. */
  orphan_since?: string | null;
  /** Backend-owned live paths for this asset identity, including the primary. */
  asset_occurrences?: AssetOccurrence[];
  /** Number of non-primary live paths. The frontend displays, but never derives, it. */
  duplicate_count?: number;
  created_by?: string;
  created_date?: Date;
  updated_by?: string;
  updated_date?: Date;
  created_through?: string;
  updated_through?: string;
  schema_version?: string;
  /**
   * Epoch-ms of this entity's last activation, stamped server-side by the
   * generic `activate` action (`Entity.last_active_at`, entity_model.py:375).
   * A base-entity field the wire sends for EVERY type, and `IEntity` already
   * declared it — but `APIEntity` did not, so `Project.last_active_at` (the
   * open-recency sort in the project picker) read as "does not exist" even
   * though `deepAssign` puts it on the instance.
   */
  last_active_at?: number | string | null;
  labels?: string[];
  root_vfs_path?: string;
  fs_storage_mount_path?: string | null;
  visitor_roles?: string[];
  authenticated_roles?: string[];
  public_roles?: string[];
  /** Epoch-ms of the last successful content edit. */
  last_edited_at?: number | string | null;
  /**
   * Hub role roster cache: one row per member with their hub-set role. Membership
   * is a generic capability of any remote entity; the hub is the source of truth
   * (RoleRelationship edges) and this is a READ CACHE. ``deepAssign`` populates it
   * from the wire ``members`` field.
   */
  members?: EntityMember[];
  _expand?: EntityExpansion;
  _dirty: boolean = true;
  _typeId: TypeId | null = null;
  /**
   * Wire-bound context references. Populated from the incoming
   * ``shared_context_entities`` wire field on deserialize; emitted back on
   * serialize. The public read accessor is ``sharedContextEntities``.
   *
   * Frontend code does NOT mutate this directly — to publish a link, call
   * the backend ``share-context`` action.
   */
  private _shared_context_entities_: TypeId[] = [];
  /**
   * Backend-computed private context. The Python side merges implicit
   * projections (project_id) with explicit attachments and ships the
   * deduped array over the wire — this slot just stores what arrived.
   * The FE never reads or mutates raw explicit attachments independently.
   * Read via ``privateContextEntities`` (identity getter).
   */
  private _private_context_entities_: TypeId[] = [];
  /**
   * Per-entry sidecar storage harvested by the backend at detection time.
   * Keyed by ``str(typeid)`` (e.g. ``"plan-b034e56e-..."``). Mirrors the
   * Python-side ``shared_context_entity_data`` / ``private_context_entity_data``
   * fields. For file-backed types this typically holds ``{path}`` so a dock
   * loader 404 can self-heal via ``?hint_path=...`` without a reverse-id
   * lookup. Read via ``getContextEntryData(typeid)``.
   */
  private _shared_context_entity_data: Record<string, Record<string, unknown>> = {};
  private _private_context_entity_data: Record<string, Record<string, unknown>> = {};
  _isLoaded: boolean = false;
  static nextInstanceIndex: number = 0;
  _instanceIndex: number = APIEntity.nextInstanceIndex++;
  protected _loadingPromise: Promise<void> | null = null;

  // Event emitter system
  protected _eventListeners: Map<string, Callable[]> = new Map();

  // Default FlowData stream for receiving WebSocket FlowData notifications
  protected _flowDataStream: FlowDataStream | null = null;

  get dirty(): boolean {
    return this._dirty;
  }

  get i(): number {
    return this._instanceIndex;
  }

  set dirty(value: boolean) {
    this._dirty = value;
  }

  _icon?: string | null;

  /**
   * This row's icon: a per-ROW `icon` from the wire when the backend sends one
   * (`Organization.icon` is a real `APIField(None)`), otherwise the TYPE's
   * registry glyph (`static icon`, from `TypeInfo.icon`).
   *
   * This USED to be a getter with no setter, which made `deepAssign` throw
   * `Cannot set property icon of #<APIEntity> which has only a getter` for any
   * entity whose payload carried an `icon` key — `new Team({icon})` crashed.
   * The pair is what the two-level fallback needed all along.
   */
  get icon(): string | null | undefined {
    return this._icon ?? (this.constructor as typeof APIEntity).icon;
  }

  set icon(value: string | null | undefined) {
    this._icon = value ?? null;
  }

  get editorDockPointer(): DockPointerData {
    throw new Error(`editorDockPointer not implemented for ${this.constructor.name}`);
  }

  get saved(): boolean {
    // Hub mirrors intentionally omit ``created_by``: attribution is PRIVATE.
    // ``created_date`` is HUB_READ and is stamped for every durable row,
    // including creator-less remote reflections. Recognize either marker so a
    // received entity updates its existing id via PUT instead of being POSTed
    // as a new, local-only entity.
    return (
      (this.created_by !== undefined && this.created_by !== null) ||
      (this.created_date !== undefined && this.created_date !== null)
    );
  }

  get isLoaded(): boolean {
    return this._isLoaded;
  }

  get identifier(): string {
    return this.uname ? `@${this.uname}` : this.id;
  }

  /**
   * Human-readable label for this entity. Used at every user-visible name
   * display site. Composed from ``getDisplayName()`` (subclass override hook,
   * defaults to ``null``) falling through to ``defaultDisplayName`` (the
   * universal chain over ``name`` → ``uname`` → ``title`` → ``<type>-<key>``
   * → ``<type>-<id-tail>``).
   *
   * Subclasses customize by overriding ``getDisplayName()``, NOT this getter.
   * Returning ``null`` from ``getDisplayName`` defers to the default chain.
   */
  get displayName(): string {
    return this.getDisplayName() ?? this.defaultDisplayName;
  }

  /**
   * Subclass override hook. Return a custom display string when the default
   * chain isn't what the entity wants (e.g. Project's cwd-basename branch,
   * CollaborationRoom's participant join). Return ``null`` to defer to
   * ``defaultDisplayName``. Base returns ``null``.
   */
  getDisplayName(): string | null {
    return null;
  }

  /**
   * Universal fallback chain. Read in order; first non-empty rung wins.
   *   1. ``this.name`` if non-empty (after trim)
   *   2. ``this.uname`` if non-empty
   *   3. first 2 words of ``this.title`` + ' …' (or just the title if ≤ 2 words)
   *   4. ``<type>-<key>`` if ``this.key`` is non-empty
   *   5. ``<type>-<id[0:4]>…<id[-4:]>`` (or ``<type>-<id>`` literal when id < 8 chars)
   */
  get defaultDisplayName(): string {
    const type = (this.constructor as typeof APIEntity).type ?? 'entity';

    // 1. name
    if (isNonEmptyString(this.name)) return this.name;

    // 2. uname
    if (isNonEmptyString(this.uname)) return this.uname;

    // 3. title prefix
    if (isNonEmptyString(this.title)) {
      const words = this.title.trim().split(/\s+/);
      const head = words.slice(0, 2).join(' ');
      return words.length > 2 ? `${head} …` : head;
    }

    // 4. <type>-<key>
    if (isNonEmptyString(this.key)) return `${type}-${this.key}`;

    // 5. <type>-<id-tail>
    const id = this.id ?? '';
    if (id.length < 8) return `${type}-${id || '?'}`;
    return `${type}-${id.slice(0, 4)}…${id.slice(-4)}`;
  }

  /**
   * True when `displayName` is ONLY the `<type>-<key>` / `<type>-<id-tail>`
   * synthetic fallback — i.e. the entity has no real `name`/`uname`/`title`/`key`
   * and no `getDisplayName()` override. Distinguishes "the label is a real name"
   * from "the label is a fabricated id string" without string-sniffing the value
   * (which would risk eating a legit user name that happens to look like one).
   *
   * Callers that must not persist or label with the synthetic — notably the tab
   * name resolver (`FlowSync.getTabName`), which would otherwise freeze
   * `agentic_process-<id>` into `Tab.name` — check this and fall back instead.
   */
  get hasSyntheticDisplayName(): boolean {
    return (
      this.getDisplayName() == null &&
      !isNonEmptyString(this.name) &&
      !isNonEmptyString(this.uname) &&
      !isNonEmptyString(this.title) &&
      !isNonEmptyString(this.key)
    );
  }

  get expand(): EntityExpansion | undefined {
    return this._expand;
  }

  set expand(value: EntityExpansion | undefined) {
    this._expand = value;
  }

  get expansions(): ExpansionType[] {
    return this.expand?.expansions ?? [];
  }

  set expansions(expansions: ExpansionType[]) {
    if (!this.expand) {
      this.expand = { expansions: [] };
    }

    if (!this.expand.expansions) {
      this.expand.expansions = [];
    }
    if (!expansions) return;

    // Merge unique values
    this.expand.expansions = [...new Set([...this.expand.expansions, ...expansions])];
  }

  isExpanded(expansion: ExpansionType | ExpansionType[] | ExpansionRequest): boolean {
    if (!expansion) return true;
    if (Array.isArray(expansion)) {
      return expansion.every((e) => this.isExpanded(e));
    }
    if (expansion instanceof ExpansionRequest) {
      if (!expansion.expand) return true;
      return expansion.expand?.every((e) => this.isExpanded(e)) ?? false;
    }
    return this.expand?.expansions?.includes(expansion) ?? false;
  }

  setExpansion(expansion: ExpansionType) {
    if (this.isExpanded(expansion)) return;

    if (!this.expand) {
      this.expand = { expansions: [] }; // Ensure `expand` is initialized
    }
    if (!this.expand.expansions) {
      this.expand.expansions = [];
    }

    this.expand.expansions.push(expansion);
  }

  get is_private(): boolean | undefined {
    return this.expand?.is_private;
  }

  set is_private(value: boolean | undefined) {
    this.setExpansion('is_private');
    this.expand!.is_private = value;
  }

  get ImOwner(): boolean {
    return this.expand?.roles?.includes('owner') || false;
  }

  get ImAdmin(): boolean {
    return roleCapabilities(this.expand?.roles ?? []).has('admin');
  }

  get ImEditor(): boolean {
    return roleCapabilities(this.expand?.roles ?? []).has('editor');
  }

  get ImReader(): boolean {
    return roleCapabilities(this.expand?.roles ?? []).has('reader');
  }

  get ImRunner(): boolean {
    return roleCapabilities(this.expand?.roles ?? []).has('runner');
  }

  isAllowedAction(action: ActionType): boolean {
    if (!this.isExpanded('permissions')) {
      throw new Error(`Permissions not expanded on entity ${this.getType()}, can not check action permissions`);
    }
    const allowed = (this.expand?.allowed_actions ?? []) as string[];
    // An owner's expansion names a type's own actions type-qualified (`llm_endpoint.allocate`),
    // an admin's plain (`allocate`): either answers yes.
    return allowed.includes(action) || allowed.includes(`${this.getType()}.${action}`);
  }

  addToAllowedActions(action: ActionType) {
    this.setExpansion('permissions');
    if (this.isAllowedAction(action)) return;
    if (this.expand && !this.expand.allowed_actions) {
      this.expand.allowed_actions = [];
    }
    if (!this.expand?.allowed_actions?.includes(action)) {
      this.expand?.allowed_actions?.push(action);
    }
  }

  get readOnly(): boolean {
    if (!this.saved) return false;
    return !this.isAllowedAction('update');
  }

  get canCreate(): boolean {
    if (!this.saved) return false;
    return this.isAllowedAction('create');
  }

  get canInvite(): boolean {
    if (!this.saved) return false;
    return this.isAllowedAction('members');
  }

  get canDelete(): boolean {
    if (!this.saved) return false;
    return this.isAllowedAction('delete');
  }

  get canGetRelatedWorkspace(): boolean {
    if (!this.saved) return false;
    return this.isAllowedAction('get_related_workspace');
  }

  get scopeWorkspaces(): TypeId[] {
    if (!this.expand?.auth_scopes) {
      return [];
    }
    const workspaces: TypeId[] = [];
    this.expand?.auth_scopes.forEach((scope) => {
      scope.forEach((raw) => {
        const tid = new TypeId(raw);
        if (tid.type === EntityTypes.Workspace) {
          workspaces.push(tid);
        }
      });
    });
    return workspaces;
  }

  constructor(entityJson: any = {}) {
    dataManager.deepAssign(this, entityJson);
    this.id = entityJson.id || uuidv4();
    if (entityJson.type && entityJson.type != this.getType()) {
      throw new Error(`Entity type mismatch: ${entityJson.type} != ${this.getType()}`);
    }
    // Move the wire-shaped ``shared_context_entities`` and the
    // backend-computed ``private_context_entities`` into private storage,
    // then drop the public aliases so toJSON's dynamic-property iterator
    // doesn't double-emit them.
    //
    // IMPORTANT — ``private_context_entities`` arrives ALREADY merged from
    // the backend: implicit projections (project_id) + explicit raw
    // attachments + dedup. The FE just deserializes the final list and
    // renders it. Computation lives server-side in
    // ``Entity.get_implicit_private_context_entities`` /
    // ``private_context_entities`` (Pydantic computed_field).
    const fromWireShared = (this as any).shared_context_entities as Array<unknown> | undefined;
    if (Array.isArray(fromWireShared) && fromWireShared.length > 0) {
      this._shared_context_entities_ = fromWireShared.map((v) => (v instanceof TypeId ? v : new TypeId(String(v))));
    }
    delete (this as any).shared_context_entities;

    const fromWirePrivate = (this as any).private_context_entities as Array<unknown> | undefined;
    if (Array.isArray(fromWirePrivate) && fromWirePrivate.length > 0) {
      this._private_context_entities_ = fromWirePrivate.map((v) => (v instanceof TypeId ? v : new TypeId(String(v))));
    }
    delete (this as any).private_context_entities;

    // Per-entry sidecar data (shared + private buckets). Same lift-into-
    // private-storage / drop-public-alias dance as the typeid arrays above
    // so toJSON's dynamic iterator doesn't double-emit them.
    const fromWireSharedData = (this as any).shared_context_entity_data as
      | Record<string, Record<string, unknown>>
      | undefined;
    if (fromWireSharedData && typeof fromWireSharedData === 'object') {
      this._shared_context_entity_data = { ...fromWireSharedData };
    }
    delete (this as any).shared_context_entity_data;

    const fromWirePrivateData = (this as any).private_context_entity_data as
      | Record<string, Record<string, unknown>>
      | undefined;
    if (fromWirePrivateData && typeof fromWirePrivateData === 'object') {
      this._private_context_entity_data = { ...fromWirePrivateData };
    }
    delete (this as any).private_context_entity_data;

    const proxy = getProxy(this);
    const replaced = dataManager.getByTypeIdFromCache(this.typeId) as Record<PropertyKey, unknown> | null;
    if (replaced && replaced !== proxy) {
      replacedByConstruction.set(this, replaced);
      queueMicrotask(() => replacedByConstruction.delete(this));
    }
    dataManager.register_new_entity(this.typeId, proxy);
    return proxy;
  }

  public get typeId(): TypeId {
    if (!this.id) {
      throw new Error('Entity has no ID');
    }
    if (!this.getType()) {
      throw new Error('Entity has no type');
    }
    if (!this._typeId) {
      this._typeId = new TypeId(this.getType(), this.identifier);
    }
    return this._typeId;
  }

  /**
   * The dock that opens this entity.
   *
   * Declared `IDockPointer | null` rather than `DockPointerData` because ONE
   * subclass legitimately has no dock: `Tab.dockPointer` parses a stored
   * pointer string and returns null when there is none or it will not parse.
   * Its consumers have always null-checked (`if (!tab?.dockPointer) return`) —
   * only this declaration disagreed with them.
   *
   * The 28 subclasses that always produce a pointer keep narrowing their own
   * override back to `DockPointerData`, so code holding a concrete `Shell`,
   * `Agent` or `Conversation` still gets a non-null type. The null only shows
   * up when you read `.dockPointer` off a generic entity — where it might in
   * fact be a Tab, so the check belongs there.
   */
  public get dockPointer(): IDockPointer | null {
    return this.defaultDockPointer;
  }

  /**
   * The generic "open this entity" dock, always non-null.
   *
   * Subclasses that compute a better pointer fall back to THIS rather than to
   * `this.defaultDockPointer` — a named member instead of reaching back through the
   * member they are overriding, and one that keeps its non-null type where
   * `dockPointer` itself cannot (see above).
   */
  protected get defaultDockPointer(): DockPointerData {
    return new DockPointerData(ViewType.HOME, this.typeId?.toString());
  }

  /**
   * POST one of this entity's actions (`/graph/<type>/<id>/<action>`) with an
   * optional JSON body and return the envelope's `data`. The one helper every
   * entity's action methods share (`DataSource.pollNow`, `Dataset.promote`, …).
   */
  protected post<R>(action: string, body?: Record<string, unknown>): Promise<R> {
    const info = new ActionInfo(action, this.getType(), this.id, 'POST' as HttpMethod);
    if (body) info.bodyParameters = body;
    return dataManager.callAction<undefined, R>(info);
  }

  /** GET one of this entity's actions (with `query` as its query string) and return the envelope's `data`. */
  protected get<R>(action: string, query?: Record<string, unknown>): Promise<R> {
    const info = new ActionInfo(action, this.getType(), this.id, 'GET' as HttpMethod);
    if (query && Object.keys(query).length) info.queryParameters = query;
    return dataManager.callAction<undefined, R>(info);
  }

  /**
   * Asset-editor dock pointer for a file-backed asset entity, or null when it
   * has no asset file yet. Asset subclasses return this from `dockPointer`
   * (falling back to `this.defaultDockPointer`); the `editor/<type>/<ref>` format
   * lives here once so it stays consistent across every asset type.
   *
   * `typeSegment` defaults to this entity's own type, which is what all 32 call
   * sites passed as a hand-written literal. A typo in one of those was silent —
   * `editorForType` just returns undefined and the entity gets a permanently
   * inert editor link.
   */
  protected assetEditorPointer(typeSegment: string = this.getType()): DockPointerData | null {
    const editor = editorForType(typeSegment);
    if (!editor) return null;
    // Stable typeid form: editor/<editor>/typeid/<type>-<id>. `this.typeId`
    // throws if the entity has no id → fall back to null (callers use ?? super).
    try {
      return new DockPointerData(ViewType.ASSETS, `editor/${editor}/typeid/${this.typeId.toString()}`);
    } catch {
      return null;
    }
  }

  /**
   * Where a search hit for this entity navigates. Always non-null: a result row
   * has to go SOMEWHERE, and the generic "open this entity" dock is the right
   * floor. This used to return `this.dockPointer` while declaring
   * `DockPointerData`, so for the one entity whose pointer can be absent (a Tab
   * with no stored pointer, or one that will not parse) it handed back a null
   * its callers were typed never to receive.
   */
  public get searchDockPointer(): DockPointerData | IDockPointer {
    return this.dockPointer ?? this.defaultDockPointer;
  }

  /**
   * Navigate the dock to this entity. Delegates to the active
   * NavigationActions instance registered as the ``navigation`` global by
   * ``useDockNavigation`` in the UI. No-op when no navigation is available
   * (non-UI contexts, before first render).
   */
  public openDock(extraOptions?: Record<string, string>): void {
    const nav = (window as any).navigation as
      | { openDock: (pointer: IDockPointer, extraOptions?: Record<string, string>) => void }
      | undefined;
    // No dock is the same no-op as no navigation: an entity that does not
    // address one (a Tab whose stored pointer is missing or unparseable) has
    // nowhere to send the dock.
    const dock = this.dockPointer;
    if (!dock) return;
    nav?.openDock(dock, extraOptions);
  }

  public clone(): T {
    // Get the full JSON representation of the current entity
    const jsonData = this.toJSON();

    // Remove properties that should be unique or reset for the clone
    jsonData.id = uuidv4();
    delete jsonData.created_by;
    delete jsonData.created_date;
    delete jsonData.updated_by;
    delete jsonData.updated_date;
    delete jsonData.schema_version;

    // Create a new instance of the same type with the cloned data
    const Constructor = this.constructor as new (json: any) => T;
    return new Constructor(jsonData);
  }

  public toJSON(): any {
    // Create a basic object with known properties
    const baseObject: any = {
      created_by: this.created_by,
      created_date: this.created_date,
      updated_by: this.updated_by,
      updated_date: this.updated_date,
      id: this.id,
      type: this.getType(),
      version: this.schema_version,
      // Wire-bound context only. Private context stays local — it's never
      // published or sent to the backend, by design. Direct-field projections
      // (project_id, assignee, ...) are emitted as their own typed fields by
      // the dynamic-property iterator below; the ``privateContextEntities``
      // getter folds them in at read time on this client only.
      shared_context_entities: this._shared_context_entities_.map((t) => t.toString()),
      // Per-entry sidecar — only the shared bucket survives toJSON, matching
      // the backend's share() exclusion of the private one.
      shared_context_entity_data: { ...this._shared_context_entity_data },
    };

    // Dynamically add all enumerable properties of the instance
    // This includes properties in subclasses
    for (const key in this) {
      if (!key.startsWith('_') && this.hasOwnProperty(key) && baseObject[key] === undefined) {
        // Edit recency is patched atomically by the generic mark-edit action.
        // Echoing a cached value in a normal full-entity save could overwrite a
        // newer stamp that arrived after this entity snapshot was hydrated.
        if (SERVER_MANAGED_SAVE_FIELDS.has(key)) continue;
        if (this.schema && !this.isDbField(key)) {
          continue; // Skip non-database fields
        }

        const value: any = this[key];

        // Check if the value is an array
        if (Array.isArray(value)) {
          baseObject[key] = value.map((item) => {
            // Check if item is an object and has a toJSON method
            if (item && typeof item === 'object' && typeof item.toJSON === 'function') {
              return item.toJSON(); // Serialize nested object
            } else {
              return item; // Return simple value or non-serializable object directly
            }
          });
        } else if (value && typeof value === 'object' && typeof value.toJSON === 'function') {
          // Use the toJSON method of the nested object
          baseObject[key] = value.toJSON();
        } else {
          // Assign the value directly
          baseObject[key] = value;
        }
      }
    }

    // `icon` is a prototype ACCESSOR over `_icon` (falling back to the class's
    // static TypeInfo glyph), so the own-property loop above cannot see it and
    // the `_icon` backing field is underscore-skipped. Emit it here, under
    // exactly the filters that loop applies.
    //
    // This used to be done by installing an own enumerable accessor on every
    // instance in the constructor. Measured, that cost ~300-430ns and ~176
    // bytes per entity — on the hottest constructor in the app — and it needed
    // a prototype-chain guard so it would not shadow a subclass override.
    if (baseObject.icon === undefined && (!this.schema || this.isDbField('icon'))) {
      const icon = this.icon;
      if (icon !== undefined) baseObject.icon = icon;
    }

    // delete baseObject.expand?.roles;
    // delete baseObject.expand?.allowed_actions;
    // delete baseObject.expand?.auth_scopes;
    // delete baseObject.expand?.expansions;
    // delete baseObject.expand;
    return baseObject;
  }

  public static compare<U extends APIEntity<U>>(
    this: { new (): U },
    attribute: 'created_date' | 'updated_date',
    order: 'asc' | 'desc' = 'desc',
  ) {
    return (a: U, b: U) => {
      const aValue = a[attribute];
      const bValue = b[attribute];

      if (!aValue && !bValue) {
        return 0;
      }
      if (!aValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (!bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return order === 'asc' ? (aValue >= bValue ? 1 : -1) : aValue >= bValue ? -1 : 1;
    };
  }

  getType(): string {
    const classType = (this.constructor as typeof APIEntity).type;
    if (!classType) {
      throw new Error('Entity type not set');
    }
    return classType;
  }

  public static isType<U extends APIEntity<U>>(
    this: { new (): U; type: string },
    entity: AnyEntity | null,
  ): entity is U {
    return entity?.getType() === this.type;
  }

  public static getByIdFromCache<U extends APIEntity<U>>(this: { new (): U; type: string }, id: string): U | null {
    return dataManager.getByTypeIdFromCache<U>(new TypeId(this.type, id));
  }

  public static async getById<U extends APIEntity<U>>(
    this: { new (): U; type: string },
    id: string,
    query?: ExpansionRequest,
  ): Promise<U | null> {
    return await dataManager.getByTypeId<U>(new TypeId(this.type, id), query);
  }

  get hasBlobs(): boolean {
    if (!this.schema) {
      console.warn('hasBlobs: Schema not found, cant check blobs');
      throw new Error('hasBlobs: Schema not found, cant check blobs');
    }
    return this.schema.hasBlobs;
  }

  public isDbField(fieldName: string): boolean {
    // The universal fields this class declares (lifted from Python
    // ``DBBaseRecord``/``Entity``) belong to EVERY entity's wire contract by
    // construction — a per-type schema is only authoritative about that type's
    // own fields. The desk publishes each type's fully-resolved schema, so the
    // distinction never showed; the hub publishes only the SUBCLASS DELTA
    // (`project` → artifacts/helpdesk/shared_*_origins), which made `name` look
    // like a non-db field and silently stripped it from every project POST/PUT
    // — a project created on the hub came back nameless.
    if (BASE_WIRE_FIELDS.has(fieldName)) return true;
    if (!this.schema) {
      console.warn('isDbField: Schema not found, cant check blobs', this.type);
      return false;
    }
    const property = this.schema.getProperty(fieldName);
    if (!property) {
      return false;
    }

    return true;
  }

  get schema(): JSONSchemaParser {
    const schema = dataManager.getSchema(this.getType());
    return schema;
  }

  public static async query<U extends APIEntity<U>>(
    this: { new (): U; type: string },
    request: QueryRequest,
    invalidate: boolean = false,
  ): Promise<U[]> {
    // Create a new QueryRequest with entity type override
    const entityRequest = new QueryRequest({
      type: this.type,
      query: request.query,
      scope: request.scope,
      callback: request.callback,
      name: request.name || `${this.type} static query`,
    });
    return await dataManager.query<U>(entityRequest, invalidate);
  }

  public static async watchQuery<U extends APIEntity<U>>(
    this: { new (): U; type: string },
    request: QueryRequest,
  ): Promise<() => void> {
    // Create a new QueryRequest with entity type override
    const entityRequest = new QueryRequest({
      type: this.type,
      query: request.query,
      scope: request.scope,
      callback: request.callback,
      name: request.name || `${this.type} static watchQuery`,
    });

    return await dataManager.watchQuery<U>(entityRequest);
  }

  public markAsExpanded(): void {
    const loadingExpansions = (this.constructor as typeof APIEntity).getLoadingExpansions();
    if (!loadingExpansions.expand) {
      loadingExpansions.expand = [];
    }
    this.expand = { ...this.expand, expansions: [...loadingExpansions.expand] };
  }

  public async save(scope: TypeId[] | TypeId = [], destination?: FSRefJson): Promise<T> {
    const isNew = !this.saved;
    if (!Array.isArray(scope)) {
      scope = [scope];
    }
    // Capture the user's intended write NOW. DataManager serializes saves per
    // entity, so this call may wait behind an older request; the shared cached
    // entity can be mutated in that interval by the older response or a WS
    // DataOp. A JSON round-trip is also the exact wire normalization Axios
    // would perform (Dates -> strings, undefined fields omitted).
    const entityJson = JSON.parse(JSON.stringify(this.toJSON())) as IEntity;
    if (destination) {
      if (!isNew) throw new Error('A creation destination cannot relocate an existing entity');
      Object.assign(entityJson, { destination });
    }
    const entity = await dataManager.save<T>(this.typeId, scope, entityJson);
    if (isNew) {
      this.markAsExpanded();
      this._isLoaded = true;
    }
    return entity;
  }

  /**
   * Push this entity to the hub via the standard graph action
   * ``POST /api/v1/graph/<type>/<id>/share``. The local backend's
   * ``share`` action handler forwards the create to the hub using the
   * stored cloud credentials. The action returns the backend's canonical
   * entity; adopt that response rather than guessing which fields changed.
   *
   * ``users`` invites recipients via the standard ``MembershipRequest``
   * pattern (one ``POST /graph/<type>/<id>/members`` per recipient — see
   * ``Conversation.share`` / ``Project.share`` on the Python side). Each
   * entry is either a bare ``idOrEmail`` string — for a caller with no role
   * to send — or ``{idOrEmail, roles?}`` when one is needed. ``idOrEmail`` is
   * either an email — for anyone, including someone with no account yet —
   * or a hub user id, for a contact the address book knows only by id (the
   * hub never discloses other people's emails, so those contacts have no
   * email to invite by); the backend decides which by shape. ``role`` is
   * optional; only a Project invite honours it (one of the roles the
   * ``share-roles`` action returns — ``membershipService.fetchShareRoles`` —
   * default ``member``) — other types ignore it.
   *
   * On the wire, ``recipients`` mirrors ``users`` exactly (bare string or
   * ``{idOrEmail, role}``) — the local backend's ``share`` action validates it.
   */
  public async share(users: ShareInvitee[] = []): Promise<T> {
    const recipients = shareRecipients(users);
    const info = new ActionInfo('share', this.typeId.type, this.typeId.id, 'POST');
    info.bodyParameters = recipients.length ? { ...this.toJSON(), recipients } : {};
    // Not one fixed shape — a full entity or a bare receipt, depending on entity type — hence a guarded dict, not a named response type.
    const response = await dataManager.callAction<unknown, Record<string, unknown>>(info);
    return this.adoptShareResponse(response);
  }

  /**
   * Adopt the ``share`` action's answer: the backend's canonical entity when it
   * returned this one, else ``this`` unchanged.
   */
  protected adoptShareResponse(response: Record<string, unknown> | null | undefined): T {
    if (
      response &&
      'id' in response &&
      'type' in response &&
      String(response.id) === this.id &&
      String(response.type) === this.typeId.type
    ) {
      return dataManager.updateEntityFromJson<T>(response);
    }
    // Some specialized share actions return a receipt rather than an entity.
    // They update their local cache server-side and broadcast that canonical
    // state; crucially, do not manufacture ``remote=true`` while waiting for it.
    return this as unknown as T;
  }

  /**
   * Per-instance cache for ``fetchMembers`` so repeat reads (e.g. multiple
   * UI consumers mounting against the same entity) don't each round-trip.
   * Undefined = never fetched; an array = the last fetched roster.
   */
  private _membersCache?: EntityMember[];

  /**
   * Generic roster fetch for any shareable entity — GET ``<type>/<id>/members``.
   *
   * The ``members`` action is ``reflect="hub"`` server-side: for a ``remote``
   * entity the local server forwards to the hub and mirrors the roster back
   * onto the local row; for a local-only entity it returns the cached
   * members. Either way this returns the normalized ``EntityMember[]``.
   *
   * ``cache`` (default ``true``): return the per-instance cached roster when
   * present, so incidental reads are free. Pass ``cache: false`` to force a
   * fresh round-trip — the path a deliberate "reload" (e.g. the conversation
   * refresh button, or a post-invite/role-change refresh) must use so it
   * reflects hub state rather than a stale snapshot.
   */
  public async fetchMembers(opts: { cache?: boolean } = {}): Promise<EntityMember[]> {
    const useCache = opts.cache ?? true;
    if (useCache && this._membersCache) return this._membersCache;
    const info = new ActionInfo('members', this.typeId.type, this.typeId.id, 'GET');
    info.hubReflect = true; // roster is hub-owned — reflect this read to the hub
    const res = await dataManager.callAction<undefined, EntityMember[]>(info);
    // Defensive: the hub utils coerce empty lists to {} upstream
    // (`resp.json().get('data') or {}`); treat any non-array as "no members".
    this._membersCache = (Array.isArray(res) ? res : []).map(normalizeMember);
    return this._membersCache;
  }

  /**
   * Invite a member by email — POST ``<type>/<id>/members`` with a
   * ``MembershipRequest`` (``{recipient_email, invitation_targets:[{typeid, roles}]}``).
   * Reflected to the hub (``info.hubReflect``): for a ``remote`` entity (e.g. an
   * organization or team) the hub creates the Invitation + emails the recipient.
   * Unlike ``share([email])`` (which is Conversation-shaped and also creates the
   * remote row), this targets an already-remote entity and only invites — the
   * right call for inviting into an organization/team from the members UI.
   *
   * ``role`` defaults to ``member``. The reflection layer returns the refreshed
   * roster; seed the cache from it when it comes back as an array.
   *
   * ``opts`` are all optional and all omitted from the body when absent — an
   * always-present ``undefined`` would change the wire shape of every existing
   * caller:
   *  - ``callbackOverride``: app-relative path the recipient lands on after
   *    accepting. Without it the hub falls back to the target's entity URL,
   *    which for most types the SPA has no route for.
   *  - ``transfer`` + ``roleToKeep``: hand the entity OVER rather than share it.
   *    ``roleToKeep: null`` means keep nothing. Owner-only, enforced hub-side by
   *    ``can_assign``'s TRANSFER branch — never assume it from the UI.
   *  - ``notifyByEmail: false``: grant without emailing. The role is written —
   *    and, with auto-accept on, accepted — before the hub reaches the mail
   *    step, so this withholds the NOTIFICATION and nothing else. Only worth it
   *    where the recipient is being told another way. Sent only when explicitly
   *    false, so the body of every existing caller is unchanged.
   */
  public async inviteMember(
    /** An email — for anyone — or a hub user id, for a contact known only by id. */
    idOrEmail: string,
    roles: string[] | string = this.typeId.type === 'conversation' ? ['reader', 'runner'] : ['reader'],
    opts?: {
      callbackOverride?: string;
      transfer?: boolean;
      rolesToKeep?: string[] | null;
      /**
       * Entities to grant ALONGSIDE this one, in the same invitation.
       *
       * `invitation_targets` has always been a list the hub grants in full, and
       * ONE invitation means one email landing on one place — which is why a
       * thing that is only usable together with something else says so here
       * rather than being chased with a second invitation.
       *
       * Safe to combine with `transfer`: the hub decides the grant kind per
       * TARGET (the entity invited at `owner` is the one handed over), so a
       * companion granted at a lower role rides along as an ordinary
       * membership instead of being refused.
       */
      extraTargets?: { typeid: string; roles: string[]; is_final?: boolean }[];
      /**
       * Send the invitation email? Defaults to true (the hub's own default).
       *
       * The SENDER's choice, not the recipient's — the hub keeps this separate
       * from `is_notification_required`, which is the recipient's preference.
       */
      notifyByEmail?: boolean;
    },
  ): Promise<void> {
    const info = new ActionInfo('members', this.typeId.type, this.typeId.id, 'POST');
    info.hubReflect = true; // membership change is hub-owned — reflect to the hub
    info.bodyParameters = {
      ...(idOrEmail.includes('@')
        ? { recipient_email: normalizeEmail(idOrEmail) ?? '' }
        : { recipient_user_id: idOrEmail.replace(/^user-/, '') }),
      invitation_targets: [{ typeid: `${this.typeId.type}-${this.typeId.id}`, roles: normalizeMembershipRoles(roles) }, ...(opts?.extraTargets ?? []).map(target => ({...target, roles: normalizeMembershipRoles(target.roles)}))],
      ...(opts?.callbackOverride ? { callback_override: opts.callbackOverride } : {}),
      ...(opts?.transfer ? { transfer: true, roles_to_keep: normalizeRetainedRoles(opts.rolesToKeep) } : {}),
      ...(opts?.notifyByEmail === false ? { notify_by_email: false } : {}),
    };
    const res = await dataManager.callAction<unknown, EntityMember[]>(info);
    this._membersCache = Array.isArray(res) ? res : undefined;
  }

  /**
   * Grant a role to a GROUP principal (a Team or an Organization) on this entity —
   * POST ``<type>/<id>/members`` with ``{principal, invitation_targets}``.
   *
   * The group counterpart of {@link inviteMember}, and deliberately a separate
   * method: a person is *invited* (an Invitation is minted, an email goes out, the
   * grant lands only on accept), whereas a group is *granted* immediately — it has
   * no mailbox, no account to provision and nothing to accept. The hub refuses a
   * body carrying both a `recipient_email` and a `principal` for exactly that
   * reason, so the two paths never blur.
   *
   * Direction, since it is easy to get backwards: this makes `principal` a MEMBER
   * OF this entity (a class inside a school), not the other way round.
   */
  public async addGroupMember(principal: TypeId, roles: string[] | string = ['reader']): Promise<void> {
    const info = new ActionInfo('members', this.typeId.type, this.typeId.id, 'POST');
    info.hubReflect = true; // membership change is hub-owned — reflect to the hub
    info.bodyParameters = {
      principal: `${principal.type}-${principal.id}`,
      invitation_targets: [{ typeid: `${this.typeId.type}-${this.typeId.id}`, roles: normalizeMembershipRoles(roles) }],
    };
    const res = await dataManager.callAction<unknown, EntityMember[]>(info);
    this._membersCache = Array.isArray(res) ? res : undefined;
  }

  /**
   * Remove a member by user id — DELETE ``<type>/<id>/members``. OWNER ONLY:
   * the hub enforces the owner gate (``delete_membership`` → 403 for non-owners
   * / owner-self), so this surfaces that as a thrown error rather than
   * silently no-op'ing. The DELETE body is a member selector — ``{user_id}``
   * (the hub also accepts ``{user_email}`` / ``{invitation_id}`` for pending
   * invitees that have no account yet, and still tolerates the legacy
   * ``{member_through, value}`` envelope during the transition).
   *
   * The reflection layer re-fetches the canonical roster after the remove and
   * returns it as the action response — seed the cache from it so the next
   * ``fetchMembers`` is free (falls back to invalidation on any other shape).
   */
  public async removeMember(userId: string): Promise<void> {
    // Never send an empty selector: a blank id serializes to ``{}`` and the hub
    // rejects it ("At least one of 'user_id'…"). Fail loudly at the source
    // instead of round-tripping a body-less DELETE.
    const id = userId.trim();
    if (!id) throw new Error('removeMember: a non-empty user id is required');
    const info = new ActionInfo('members', this.typeId.type, this.typeId.id, 'DELETE');
    info.hubReflect = true; // membership change is hub-owned — reflect to the hub
    info.bodyParameters = { user_id: id };
    const res = await dataManager.callAction<unknown, EntityMember[]>(info);
    this._membersCache = Array.isArray(res) ? res : undefined;
  }

  /**
   * Change a member's role — PUT ``<type>/<id>/members`` with ``{user_id, role}``.
   * The hub gates this via its role-grant chokepoint (``can_assign``): the
   * caller may only assign a role strictly below their own, on a member ranked
   * strictly below their own, never on themselves and never on the owner —
   * denials surface here as thrown errors (403). ``role`` is a lowercase
   * policy role (e.g. ``admin`` / ``editor`` / ``member`` / ``reader``).
   *
   * The reflection layer re-fetches the canonical roster after the change and
   * returns it as the action response — seed the cache from it so the next
   * ``fetchMembers`` is free (falls back to invalidation on any other shape).
   */
  public async setMemberRole(userId: string, roles: string[] | string): Promise<void> {
    const info = new ActionInfo('members', this.typeId.type, this.typeId.id, 'PUT');
    info.hubReflect = true; // role change is hub-owned — reflect to the hub
    info.bodyParameters = { user_id: userId, roles: normalizeMembershipRoles(roles) };
    const res = await dataManager.callAction<unknown, EntityMember[]>(info);
    this._membersCache = Array.isArray(res) ? res : undefined;
  }

  /**
   * True when this entity has a hub-side counterpart at the same id. Mirrors
   * the Python ``Entity.remote`` field; flipped to ``true`` by ``share()``.
   */
  remote?: boolean;

  /**
   * True when this asset is listed in its project's manifest
   * (`agentic-assets/project_manifest/project_manifest.json`). Mirrors the
   * Python `Entity.published` cache; the manifest FILE is the truth, and the
   * flag is flipped only by adopting the canonical row `setPublished()` returns.
   */
  published?: boolean;

  /**
   * Canonical parent reference ("<type>-<id>"). Single source of truth for
   * parentage; supersedes the legacy per-type ``data.parent_id``.
   */
  parent_type_id?: string | null;

  /**
   * Folder-like containment (docs/entities-groups.md): the Group this entity
   * lives in; null = ungrouped. Reflected off the wire; mutate only via
   * {@link setGroup} so backend validation (target exists, same project, no
   * cycles) runs.
   */
  group_id?: string | null;

  /**
   * Move this entity into a Group (or ungroup with null) via the generic
   * ``set-group`` action. The new membership reflects back through the
   * ordinary entity-update path; no local write.
   */
  public async setGroup(groupId: string | null): Promise<void> {
    const info = new ActionInfo('set-group', this.typeId.type, this.typeId.id, 'POST');
    info.bodyParameters = { group_id: groupId };
    await dataManager.callAction<unknown, unknown>(info);
  }

  /** Resolve a reference relative to this entity through the shared backend resolver. */
  public async resolveDisplayTarget(link: string): Promise<ShowTarget | null> {
    const info = new ActionInfo('resolve-display-target', this.typeId.type, this.typeId.id, 'POST');
    info.bodyParameters = { link };
    return (await dataManager.callAction<unknown, ShowTarget>(info)) ?? null;
  }

  /**
   * Open / set up this entity — the reception hook reused for the open-an-existing
   * surfaces (artifact favorites/cards/chips, skill run). Returns the DisplayTarget
   * to navigate to (`openDisplayTarget`): the entity itself, or a spawned Vibe setup
   * session. `projectId` optionally overrides the entity's own project binding.
   *
   * Named for the backend hook (`Entity.setup_on_receive`) rather than the wire
   * action, which stays `setup`. A bare `setup` here collided with two real,
   * differently-shaped subclass actions — `Capability.setup` (a type-specific
   * `@action.post("setup")` returning a CapabilityCheck) and
   * `ComputeNode.setup` (a different endpoint entirely, `ops/setup`, returning
   * the provider id). This one had no callers, so it is the one that moved.
   */
  public async setupOnReceive(projectId?: string | null): Promise<ReceiveShowTarget | null> {
    const info = new ActionInfo('setup', this.typeId.type, this.typeId.id, 'POST');
    info.bodyParameters = { project_id: projectId ?? null };
    const res = await dataManager.callAction<unknown, { show?: ReceiveShowTarget | null }>(info);
    return res?.show ?? null;
  }

  /**
   * Stamp this tab's ``last_active_at`` (server clock, epoch-ms) via the
   * generic ``activate`` action — the tab resolver's recency seed
   * (docs/tab-management.md Part 3 §4). Loaders call this FIRE-AND-FORGET on
   * activation (alongside the synchronous in-cache ``bumpLastActive``); it
   * never touches ``tabbed`` — membership promotion is ``tabs/open`` only.
   */
  public async activate(): Promise<void> {
    const info = new ActionInfo('activate', this.typeId.type, this.typeId.id, 'POST');
    await dataManager.callAction<unknown, unknown>(info);
  }

  /**
   * Record real user editing without issuing a request for every input event.
   * Calls coalesce by canonical type + entity id and stamp server-side one
   * minute after the final call. Fire-and-forget by design; a pending mark is
   * not flushed during a hard page close.
   */
  public static markEditById(this: { type: string }, id: string): void {
    dataManager.markEdit(new TypeId(this.type, id));
  }

  public markEdit(): void {
    const entityClass = this.constructor as typeof APIEntity & { type: string };
    entityClass.markEditById(this.id);
  }

  /** POST /entity-event {event, payload}. Unknown events are a server-side no-op. */
  public async entityEvent(event: string, payload: Record<string, unknown> = {}): Promise<unknown> {
    return APIEntity.entityEvent(this.typeId, event, payload);
  }

  /** Static form for callsites that hold only a TypeId. */
  public static async entityEvent(
    typeId: TypeId,
    event: string,
    payload: Record<string, unknown> = {},
  ): Promise<unknown> {
    const info = new ActionInfo('entity-event', typeId.type, typeId.id, 'POST');
    info.bodyParameters = { event, payload };
    return await dataManager.callActionPreferWS<unknown, unknown>(info);
  }

  public async delete(): Promise<void> {
    console.log(
      `🗑️ [APIEntity.delete] Starting deletion of ${this.typeId.type}:${this.typeId.id} (${this.constructor.name})`,
    );
    try {
      const result = await dataManager.delete(this.typeId);
      console.log(`✅ [APIEntity.delete] Successfully deleted ${this.typeId.type}:${this.typeId.id}`);
      return result;
    } catch (error) {
      console.error(`❌ [APIEntity.delete] Failed to delete ${this.typeId.type}:${this.typeId.id}:`, error);
      throw error;
    }
  }

  public subscribe(callback?: (entity: T | null) => void | Promise<void>): () => void {
    return dataManager.subscribe<T>(this.typeId, callback, true);
  }

  public async watch(): Promise<() => Promise<void>> {
    return await dataManager.watch(new TypeId(this.getType(), this.id));
  }

  /**
   * Outgoing wiki links from this entity. Hits
   * `GET /api/v1/graph/{type}/{id}/wiki/links`.
   */
  public async getLinks(): Promise<WikiLink[]> {
    const info = new ActionInfo('wiki', this.typeId.type, this.typeId.id, 'GET');
    info.subpath = 'links';
    return (await dataManager.callAction<undefined, WikiLink[]>(info)) ?? [];
  }

  /**
   * Inbound wiki links pointing at this entity. Hits
   * `GET /api/v1/graph/{type}/{id}/wiki/backlinks`.
   */
  public async getBacklinks(): Promise<WikiLink[]> {
    const info = new ActionInfo('wiki', this.typeId.type, this.typeId.id, 'GET');
    info.subpath = 'backlinks';
    return (await dataManager.callAction<undefined, WikiLink[]>(info)) ?? [];
  }

  /**
   * Re-extract this entity's outgoing wiki edges. Mirrors `Entity.reindex` on
   * the Python side. Hits `POST /api/v1/graph/{type}/{id}/wiki/reindex`.
   *
   * Pass `body` when the caller already has the markdown text in hand
   * (e.g. the editor toolbar after an out-of-band wikilink insert) so the
   * server doesn't need to re-read the record from disk.
   */
  public async reindex(body?: string): Promise<WikiLink[]> {
    const info = new ActionInfo('wiki', this.typeId.type, this.typeId.id, 'POST');
    info.subpath = 'reindex';
    if (body !== undefined) info.bodyParameters = { body };
    return (await dataManager.callAction<undefined, WikiLink[]>(info)) ?? [];
  }

  public async get_related_workspace(): Promise<Workspace | undefined> {
    if (!this.saved) return undefined;
    const actionInfo = new ActionInfo('get_related_workspace', this.typeId.type, this.typeId.id, 'GET');
    // The action answers wire JSON, not a hydrated entity — hence `IWorkspace`.
    const ws = await dataManager.callAction<undefined, IWorkspace>(actionInfo);
    if (!ws) return undefined;
    const cached = ws.id ? dataManager.getByTypeIdFromCache<Workspace>(new TypeId(EntityTypes.Workspace, ws.id)) : null;
    return cached ?? (EntityFactory.createEntity({ ...ws, type: EntityTypes.Workspace }) as Workspace);
  }

  public findWorkspaceScope(workspaceTypeId: TypeId | null): TypeId[] {
    if (!this.expand?.auth_scopes) {
      return [];
    }
    const parsedScopes: TypeId[][] = this.expand.auth_scopes.map((scope) => scope.map((raw) => new TypeId(raw)));
    let selectedScope = parsedScopes.find((scope) =>
      workspaceTypeId
        ? scope.some((typeId) => typeId.id === workspaceTypeId.id && typeId.type === workspaceTypeId.type)
        : false,
    );
    if (!selectedScope && parsedScopes.length > 0) {
      selectedScope = parsedScopes[0];
    }
    return selectedScope || [];
  }

  public async ingest(
    files: File[] | null = null,
    links: string[] | null = null,
    texts: string[] | null = null,
    resources: TypeId[] | null = null,
  ): Promise<void> {
    if (!this.saved) return undefined;
    try {
      const formData = new FormData();
      for (const file of files || []) {
        formData.append('files', file);
      }
      for (const link of links || []) {
        formData.append('links', link);
      }
      for (const text of texts || []) {
        formData.append('texts', text);
      }
      for (const resource of resources || []) {
        formData.append('resources', resource.toString());
      }
      const actionInfo = new ActionInfo('ingest', this.typeId.type, this.typeId.id, 'POST');
      actionInfo.bodyParameters = formData;
      await dataManager.callAction<FormData, undefined>(actionInfo);
    } catch (e) {
      console.error('Failed to run ingest: ', e);
    }
  }

  /**
   * Add a label to the entity locally (does not save to server).
   */
  public add_label_local(label: string): void {
    if (!this.labels) {
      this.labels = [];
    }
    // Check if label already exists
    if (!this.labels.includes(label)) {
      this.labels.push(label);
      this.dirty = true;
    }
  }

  /**
   * Remove a label from the entity locally (does not save to server). Returns true if removed, false if not found.
   */
  public remove_label_local(label: string): boolean {
    if (!this.labels) {
      return false;
    }
    const originalCount = this.labels.length;
    this.labels = this.labels.filter((l) => l !== label);
    if (this.labels.length < originalCount) {
      this.dirty = true;
      return true;
    }
    return false;
  }

  /**
   * Get all labels for the entity.
   */
  public get_labels(): string[] {
    return [...(this.labels || [])];
  }

  // ── context_entities surface ──────────────────────────────────────────
  //
  // Two buckets, split by the rule "if it came over the wire it is shared,
  // otherwise private":
  //   * ``sharedContextEntities`` — wire-bound; what every reader of this
  //     entity sees. Mutated only by the backend (call a ``share-context``
  //     action to publish).
  //   * ``privateContextEntities`` — also wire-bound now: the **backend**
  //     computes the merged list (implicit projections like ``project_id``
  //     PLUS the user's explicit attachments) via
  //     ``Entity.get_implicit_private_context_entities`` +
  //     ``private_context_entities`` Pydantic computed_field. The FE just
  //     renders the list. **The FE never combines implicit + explicit
  //     locally.** This file used to host a ``_directFieldsAsTypeIds`` hook
  //     for FE-side projection; it was removed when projection moved to
  //     the backend.

  /** Wire-bound context. Read-only on the frontend — publish via a backend
   *  ``share-context`` action. */
  public get sharedContextEntities(): TypeId[] {
    return [...this._shared_context_entities_];
  }

  /** Computed-by-backend private context (implicit projections + explicit
   *  attachments, deduped server-side). Returned as-is — no FE-side
   *  projection. */
  public get privateContextEntities(): TypeId[] {
    return [...this._private_context_entities_];
  }

  /**
   * Return the per-entry sidecar data harvested by the backend at detection
   * time (e.g. ``{path: "/Users/.../foo.md"}`` for file-backed entries).
   * Checks the private sidecar first (matches the backend precedence), then
   * shared. Returns ``undefined`` when no data was harvested for the typeid.
   *
   * Use this when rendering a chip that may navigate to an entity that
   * hasn't been indexed yet — pass ``data.path`` as ``?hint_path=...`` to
   * the entity GET so the BE can self-heal the 404 by single-file-indexing.
   */
  public getContextEntryData(typeid: TypeId | string): Record<string, unknown> | undefined {
    const key = typeid instanceof TypeId ? typeid.toString() : typeid;
    return this._private_context_entity_data[key] ?? this._shared_context_entity_data[key];
  }

  // NOTE: ``addContextEntities`` / ``removeContextEntities`` lived here as
  // FE-side mutation primitives for the private bucket. They were removed when
  // the FE became display-only for context: the FE renders whatever the backend
  // ships in ``private_context_entities`` (a Pydantic computed_field that merges
  // implicit projections + explicit attachments, server-side). To attach
  // something to a private context, use a dedicated backend action and let
  // the WS broadcast deliver the updated array. The FE never combines or
  // mutates context on its own.

  /**
   * Coerce a single TypeId or a list into a de-duplicated TypeId[]. The only
   * remaining consumer is the *shared*-bucket publish path
   * (``shareContextEntities`` / ``unshareContextEntities``), which sends the
   * targets to a backend action — it does NOT mutate local context. Kept here
   * (rather than inlined) so both share/unshare normalise identically.
   */
  private _normalizeContextEntities(input: TypeId | TypeId[]): TypeId[] {
    const list = Array.isArray(input) ? input : [input];
    const seen = new Set<string>();
    const out: TypeId[] = [];
    for (const tid of list) {
      if (!tid) continue;
      const key = tid.toString();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(tid);
    }
    return out;
  }

  private _bucketView(bucket: 'private' | 'shared' | 'both'): TypeId[] {
    if (bucket === 'shared') return this.sharedContextEntities;
    if (bucket === 'private') return this.privateContextEntities;
    return [...this.sharedContextEntities, ...this.privateContextEntities];
  }

  /** All context entries of the given type. Default 'both' returns shared first then private. */
  public contextOfType(type: string, bucket: 'private' | 'shared' | 'both' = 'both'): TypeId[] {
    return this._bucketView(bucket).filter((t) => t.type === type);
  }

  /** First context entry of the given type, or null. */
  public firstContextOfType(type: string, bucket: 'private' | 'shared' | 'both' = 'both'): TypeId | null {
    return this._bucketView(bucket).find((t) => t.type === type) ?? null;
  }

  /**
   * Publish one or many TypeIds to this entity's *shared* context via the
   * backend ``share-context`` action. The frontend can't mutate
   * ``_shared_context_entities_`` directly — sharing is a backend decision
   * — so this is the canonical publish path.
   *
   * On success the backend returns the updated shared list; we apply it
   * locally so the next render sees the change without waiting for the WS
   * broadcast. Returns the post-update list as TypeIds.
   *
   * ``opts.data`` is per-entry sidecar stored with each entry (read back via
   * ``getContextEntryData``), e.g. ``{role: 'session'}`` on a conversation.
   */
  public async shareContextEntities(
    input: TypeId | TypeId[],
    opts?: { data?: Record<string, unknown> },
  ): Promise<TypeId[]> {
    const targets = this._normalizeContextEntities(input);
    if (targets.length === 0) return [...this._shared_context_entities_];
    const info = new ActionInfo('share-context', this.typeId.type, this.typeId.id, 'POST');
    info.bodyParameters = {
      ...(targets.length === 1 ? { typeid: targets[0].toString() } : { typeids: targets.map((t) => t.toString()) }),
      ...(opts?.data ? { data: opts.data } : {}),
    };
    const result = await dataManager.callAction<
      { typeid?: string; typeids?: string[]; data?: Record<string, unknown> },
      {
        ok: boolean;
        id: string;
        type: string;
        shared_context_entities: string[];
        shared_context_entity_data?: Record<string, Record<string, unknown>>;
      }
    >(info);
    if (result?.shared_context_entity_data) this._shared_context_entity_data = { ...result.shared_context_entity_data };
    return this._applySharedFromResponse(result?.shared_context_entities);
  }

  /**
   * Remove one or many TypeIds from this entity's *shared* context via the
   * backend ``unshare-context`` action. Mirror of ``shareContextEntities``.
   */
  public async unshareContextEntities(input: TypeId | TypeId[]): Promise<TypeId[]> {
    const targets = this._normalizeContextEntities(input);
    if (targets.length === 0) return [...this._shared_context_entities_];
    const info = new ActionInfo('unshare-context', this.typeId.type, this.typeId.id, 'POST');
    info.bodyParameters =
      targets.length === 1 ? { typeid: targets[0].toString() } : { typeids: targets.map((t) => t.toString()) };
    const result = await dataManager.callAction<
      { typeid?: string; typeids?: string[] },
      { ok: boolean; id: string; type: string; shared_context_entities: string[] }
    >(info);
    return this._applySharedFromResponse(result?.shared_context_entities);
  }

  /** Rebuild ``_shared_context_entities_`` from the wire response of a
   *  share/unshare-context call, notify subscribers, and return the new list. */
  private _applySharedFromResponse(payload: string[] | undefined): TypeId[] {
    if (!Array.isArray(payload)) return [...this._shared_context_entities_];
    const parsed: TypeId[] = [];
    for (const raw of payload) {
      try {
        parsed.push(new TypeId(raw));
      } catch {
        // Skip malformed entries from server.
      }
    }
    this._shared_context_entities_ = parsed;
    dataManager.notifyPropertyChanged(this.typeId, 'shared_context_entities');
    return [...parsed];
  }

  /**
   * Add a label to the entity using the label API.
   */
  public async add_label(label: string, description?: string, color?: string): Promise<boolean> {
    try {
      // POST to label action with the new label, returns full labels list
      const actionInfo = new ActionInfo('label', this.typeId.type, this.typeId.id, 'POST');
      actionInfo.subpath = label; // Include label name in URL path
      actionInfo.bodyParameters = {
        description: description,
        color: color,
      };

      const result = await dataManager.callAction<any, string[]>(actionInfo);

      // Update local labels with the returned full list
      if (Array.isArray(result)) {
        this.labels = result;
        this.dirty = true;
      }

      return true;
    } catch (error) {
      console.error('Failed to add label:', error);
      return false;
    }
  }

  /**
   * Remove a label from the entity using the label API.
   */
  public async remove_label(label: string): Promise<boolean> {
    try {
      // DELETE to label action with the label to remove, returns full labels list
      const actionInfo = new ActionInfo('label', this.typeId.type, this.typeId.id, 'DELETE');
      actionInfo.subpath = label; // Include label name in URL path

      const result = await dataManager.callAction<any, string[]>(actionInfo);

      // Update local labels with the returned full list
      if (Array.isArray(result)) {
        this.labels = result;
        this.dirty = true;
      }

      return true;
    } catch (error) {
      console.error('Failed to remove label:', error);
      return false;
    }
  }

  /**
   * Get all available labels from the ontology.
   */
  public async get_ontology_labels(): Promise<any[]> {
    try {
      const actionInfo = new ActionInfo('ontology', this.typeId.type, this.typeId.id, 'GET');
      const result = await dataManager.callAction(actionInfo);

      return (result as any)?.labels || [];
    } catch (error) {
      console.error('Failed to get ontology labels:', error);
      return [];
    }
  }

  /**
   * Get the first and closest ancestor by type using the entity scope.
   * Uses the same logic as breadcrumbs to find ancestors.
   */
  public async get_ancestor(ancestor_type: string): Promise<AnyEntity | null> {
    if (!this.saved) {
      return null;
    }

    // Get the current workspace from dataContext (same as breadcrumb logic)
    const { dataContext } = await import('./FlowSync/context');
    const workspaceTypeId = dataContext.workspaceTypeId;

    if (!workspaceTypeId) {
      return null;
    }

    // Get expanded entity with auth_scopes (same as breadcrumb logic)
    const expandedEntity = await dataManager.getByTypeId(
      this.typeId,
      new ExpansionRequest({ expand: ['auth_scopes'] }),
    );

    if (!expandedEntity) {
      return null;
    }

    // Find the workspace scope
    const selectedScope = expandedEntity.findWorkspaceScope(workspaceTypeId);

    // Search through the scope for the first entity of the requested type
    for (const currentTypeId of selectedScope) {
      if (currentTypeId.type === ancestor_type && currentTypeId.id !== this.id) {
        const ancestorEntity = await dataManager.getByTypeId(currentTypeId);
        if (ancestorEntity) {
          return ancestorEntity;
        }
      }
    }

    return null;
  }

  /**
   * Fetch the filesystem record refs for this entity.
   * Returns a Record with recordFolderRef (record folder) and mainRef (primary content).
   * Use ref.child() to navigate further: entity.record().then(r => r.mainRef?.child("subdir"))
   */
  public async record(options: { hubReflect?: boolean } = {}): Promise<FsRecord> {
    const actionInfo = new ActionInfo('record', this.typeId.type, this.typeId.id, 'GET');
    actionInfo.subpath = 'refs';
    actionInfo.hubReflect = options.hubReflect === true;
    const result = await dataManager.callAction<void, RecordRefs>(actionInfo);
    return new FsRecord(result as RecordRefs);
  }

  /**
   * Set the public access for the entity.
   * @param isPublic - Whether the entity should be public or private.
   */
  public async setPublicAccess(isPublic: boolean): Promise<void> {
    const actionInfo = new ActionInfo('set_public_access', this.typeId.type, this.typeId.id, 'POST');
    actionInfo.bodyParameters = { is_public: isPublic };
    await dataManager.callAction<{ is_public: boolean }, { public: boolean }>(actionInfo);
  }

  /**
   * Publish / unpublish this asset into its project's manifest
   * (`POST /graph/<type>/<id>/set-published`). Resolves to the canonical row the
   * backend returns after the manifest was re-indexed — adopted into the cache,
   * which is what flips `published`; this method never writes the flag itself.
   * Refusals (not publishable, outside the project, …) reject with the backend
   * message; `code` rides on the error's `data`.
   */
  public async setPublished(published: boolean, projectId?: string | null): Promise<T> {
    const body: Record<string, unknown> = { published };
    if (projectId) body.project_id = projectId;
    const json = await this.post<Record<string, unknown>>('set-published', body);
    return dataManager.updateEntityFromJson<T>(json);
  }

  static getLoadingExpansions() {
    const expansions: ExpansionType[] = ['permissions', 'auth_scopes'];
    const schema = dataManager.getSchema(this.type!);
    if (!schema) {
      console.warn('Schema not found for', this.type!);
    }
    if (schema?.hasBlobs) {
      expansions.push('blobs');
    }
    const expansion_request = new ExpansionRequest({
      expand: [...new Set([...expansions, ...this.autoLoadExpansions])],
    });
    return expansion_request;
  }

  /**
   * Load method called once when entity is first fetched.
   * Ensures only one load operation runs at a time by reusing the same promise.
   * Subclasses should override handleLoad() to implement loading logic.
   */
  async load(): Promise<void> {
    if (!this.saved || this.isLoaded) {
      return;
    }

    if (this._loadingPromise) {
      return this._loadingPromise;
    }

    this._loadingPromise = (async () => {
      try {
        await this.handleLoad();
        this._isLoaded = true;
      } catch (error) {
        this._loadingPromise = null;
        throw error;
      }
    })();

    return this._loadingPromise;
  }

  /**
   * Protected method that subclasses override to implement entity-specific loading logic.
   * This is called by load() and should not be called directly.
   * Default implementation does nothing.
   */
  protected async handleLoad(): Promise<void> {
    // Default implementation - override in subclasses
  }

  /**
   * Reload method that explicitly calls load() regardless of isLoaded flag.
   * Use this when you need to re-initialize the entity.
   */
  async reload(): Promise<void> {
    this._isLoaded = false;
    this._loadingPromise = null;
    await this.load();
  }

  // Event emitter methods

  /**
   * Register an event listener for one or more event types
   * @param eventType - Event type or array of event types to listen to
   * @param callback - Callback function to invoke when event fires
   * @returns Unsubscribe function to remove the listener
   */
  on(eventType: string | string[], callback: Callable): () => void {
    if (!Array.isArray(eventType)) {
      eventType = [eventType];
    }

    eventType.forEach((type) => {
      if (!this._eventListeners.has(type)) {
        this._eventListeners.set(type, []);
      }
      this._eventListeners.get(type)!.push(callback);
    });

    return () => eventType.forEach((type) => this.off(type, callback));
  }

  /**
   * Remove an event listener
   * @param eventType - Event type to remove listener from
   * @param callback - The callback function to remove
   */
  off(eventType: string, callback: Callable): void {
    const listeners = this._eventListeners.get(eventType);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
        // Clean up empty listener arrays
        if (listeners.length === 0) {
          this._eventListeners.delete(eventType);
        }
      }
    }
  }

  /**
   * Emit an event to all registered listeners
   * @param eventType - Event type to emit
   * @param data - Optional data to pass to listeners
   */
  emit(eventType: string, ...args: any[]): void {
    const listeners = this._eventListeners.get(eventType);
    if (listeners) {
      // Forward ALL args, not just the first. Several call sites emit multiple
      // values — e.g. onEntityEvent → emit('entity_event', event, payload) and
      // emit('status', newStatus, oldStatus). The previous single-`data` form
      // silently dropped every argument after the first, so consumers
      // subscribing via `on('entity_event', (event, payload) => ...)` received
      // an undefined payload. Single-arg emits are unaffected (callback(arg)).
      // Per-listener isolation. `forEach` aborts on the first throw, so ONE bad
      // subscriber used to starve every subscriber registered after it — an
      // entity `delete` that threw in a view hook never reached the entity cache,
      // the tab manager, or the conversation/flow-message taps, and which
      // listeners lost it depended on mount order. Fan-out must be all-or-each,
      // never all-or-nothing.
      //
      // This isolates; it does NOT hide. The error is still reported at
      // console.error, so a throwing subscriber stays as visible as it was
      // before — it simply no longer takes the other subscribers down with it.
      // Deliberately a plain loop: `forEach` with a try inside would work, but
      // this keeps the control flow obvious at the one place it matters.
      // Indexed loop, not a spread copy: this fans out every WS entity
      // create/update/delete, so an allocation per emit is real cost on a hot path.
      // Indexing also preserves the previous `forEach` visiting semantics exactly —
      // the only intended change here is the per-listener isolation.
      for (let i = 0; i < listeners.length; i++) {
        try {
          listeners[i](...args);
        } catch (err) {
          console.error(`[EventEmitter] listener for "${eventType}" threw; other listeners still ran`, err);
        }
      }
    }
  }

  // FlowData stream methods

  /**
   * Get the default FlowData stream for this entity
   * Lazily creates the stream on first access to avoid overhead for entities that don't use it
   */
  get flowDataStream(): FlowDataStream {
    if (!this._flowDataStream) {
      this._flowDataStream = new FlowDataStream({
        id: `${this.getType()}-${this.id}-default`,
        name: `Default Stream for ${this.getType()}`,
      });
    }
    return this._flowDataStream;
  }

  /**
   * Handle incoming FlowData from WebSocket
   * Ingests to the entity's default stream (with consolidation) and emits 'flow_data' event
   * @param flowData - The FlowData to handle
   */
  handleFlowData(flowData: FlowData): void {
    this.flowDataStream.ingest(flowData);
    this.emit('flow_data', flowData);
  }

  // Cache lifetime — what the store asks an entity (FlowSync/store.ts, "one way out")

  /**
   * True once this row is finished for the UI: nothing shows it any more, so the store may
   * drop it as soon as nothing references it. False by default — a type opts in by saying
   * what "finished" means for it (a closed tab, a closed shell, an ended session whose tab
   * is gone).
   */
  isRetired(): boolean {
    return false;
  }

  /**
   * Someone is listening to this entity or to its flow-data stream — a mounted view — so
   * it is in use whatever the store's own counts say.
   */
  hasListeners(): boolean {
    return this._eventListeners.size > 0 || (this._flowDataStream?.eventNames().length ?? 0) > 0;
  }

  /**
   * The store dropped this entity for good (deleted, removed, evicted). Free what hangs
   * off it; a subclass that owns more (a PTY connection, loaded artifacts) releases that
   * too. Safe to call twice. The stream object stays, so a holder that outlived the cache
   * entry sees `'clear'` and renders empty.
   */
  onEvicted(): void {
    this._flowDataStream?.clear();
  }

  /**
   * Mirror of Python `Entity.emit_entity_event` arriving over the WS transport.
   * Dispatched by `DataManager.onFlowData` for envelopes with
   * `element_type === 'entity_event'` — these never enter the flow-data stream
   * or renderer. Default impl re-emits as the `'entity_event'` event so callers
   * can subscribe via `entity.on('entity_event', (event, payload) => ...)`.
   * Subclasses may override for entity-specific dispatch (e.g. a typed handler
   * registry).
   */
  onEntityEvent(event: string, payload: Record<string, unknown>): void {
    this.emit('entity_event', event, payload);
  }
}

// Create the singleton DataManager instance
export const dataManager = new DataManager<AnyEntity>();

// Define store as a global for console debugability
defineGlobal('store', dataManager);
